import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { GridWithCulling } from '@ff-os/shared-react/components/grid-with-culling/GridWithCulling';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { useMobile } from '@ff-os/shared-react/hooks/useMobile';
import { useSize } from '@ff-os/shared-react/hooks/useSize';
import { selectPhotoIdsByGroupId } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { range } from 'lodash';
import { selectAmountInQueueByGroupId } from '../_redux-groups';
import { PhotoPrintErrorWrapper } from './photo-print/PhotoPrintErrorWrapper';
import { PhotoPrintLoader } from './photo-print/PhotoPrintLoader';
import { PhotoPrintSpinner } from './photo-print/PhotoPrintSpinner';
import styles from './PhotoPrintsGrid.module.scss';

type ColumnCount = 2 | 3 | 4 | 5 | 6;
const allowedColumnCounts: ColumnCount[] = [2, 3, 4, 5, 6];

const minColumnWidth = 180;
const estimatedRowHeight = 250;

const _PhotoPrintsGrid = ({ amountInQueue, photoIds, renderPhoto }: Props) => {

    const [scroll, setScroll] = useState<number>(0);
    const [columns, setColumns] = useState<ColumnCount>(2);

    const container = useRef<HTMLDivElement>(null);
    const containerSize = useSize(container);

    useEffect(() => {
        if (containerSize) {
            for (const columnCount of allowedColumnCounts) {
                if (containerSize.width / columnCount >= minColumnWidth) {
                    setColumns(columnCount);
                }
            }
        }
    }, [containerSize]);

    const onScroll = (scroll: number) => {
        setScroll(scroll);
    };

    const isMobile = useMobile();

    return (
        <div className={styles.container}
            style={{ minHeight: photoIds.length <= columns ? estimatedRowHeight : 2 * estimatedRowHeight }}
            ref={container}>

            <GridWithCulling gap='m' columns={columns} expectedCellHeight={200} scroll={scroll} onScroll={onScroll}>
                {photoIds.map(id => (
                    <Cell key={id}>
                        <PhotoPrintErrorWrapper id={id} isMobile={isMobile}>
                            <PhotoPrintLoader id={id}>
                                {renderPhoto(id)}
                            </PhotoPrintLoader>
                        </PhotoPrintErrorWrapper>
                    </Cell>
                ))}

                {range(0, amountInQueue).map(i => (
                    <Cell key={i}>
                        <PhotoPrintSpinner />
                    </Cell>
                ))}
            </GridWithCulling>
        </div>
    );
};

type OwnProps = {
    groupId: string;
    renderPhoto: (id: string) => JSX.Element;
};

type Props = OwnProps & {
    photoIds: string[];
    amountInQueue: number;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    amountInQueue: selectAmountInQueueByGroupId(state, ownProps.groupId),
    photoIds: selectPhotoIdsByGroupId(state, ownProps.groupId)
});

export const PhotoPrintsGrid = connect(
    mapStateToProps
)(_PhotoPrintsGrid);